// @flow
import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {getWeekDayNames} from '../../utils/date';
import NavArrowLeftIcon from '../icons/NavArrowLeftIcon';
import NavArrowRightIcon from '../icons/NavArrowRightIcon';

export type CalendarProps = {
  onChange: (date: Date) => void,
  initialDate?: Date,
  minDate?: Date,
};

type Day = {
  dayOfMonth: number,
  isCurrentMonth: boolean,
};

function formatDay(date: Date): string {
  const intl = new Intl.DateTimeFormat('es-ES', {
    month: 'long',
    year: 'numeric',
  });

  return intl.format(date);
}

const weekDaysNames = getWeekDayNames();

function normalizeToMidnight(date: Date): Date {
  return new Date(date.setHours(0, 0, 0, 0));
}

export function generateCalendarDays(date: Date, minDate?: Date | null): Day[] {
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const startDayOfWeek = firstDayOfMonth.getDay();
  const previousMonthDisplayDays = [...Array(startDayOfWeek).keys()]
    .map(day => {
      const previousMonth = new Date(currentYear, currentMonth, -day);
      return {
        dayOfMonth: previousMonth.getDate(),
        isCurrentMonth: false,
      };
    })
    .reverse();

  const currentMonthDays = [...Array(lastDayOfMonth.getDate()).keys()].map(
    day => ({
      dayOfMonth: day + 1,
      isCurrentMonth: true,
    })
  );

  const allDays = previousMonthDisplayDays.concat(currentMonthDays);

  return allDays;
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: 8,
    borderRadius: 16,
    border: '1px solid #E8E8ED',
    width: 300,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 10,
  },
  calendarCurrentMonth: {
    fontSize: 14,
    fontWeight: '500',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
  },
  calendarNavigation: {
    display: 'flex',
    alignItems: 'center',
  },
  calendarNavigationButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    fontSize: 20,
    borderRadius: '50%',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      background: '#F2F2F2',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  todayButton: {
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 'bold',
    borderRadius: 4,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      background: '#F2F2F2',
    },
  },
  weekDayContainer: {
    display: 'grid',
    justifyContent: 'center',
    gridTemplateColumns: 'repeat(7, 38px)',
  },
  weekDay: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    fontSize: 14,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B6C80',
  },
  dayContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 38px)',
    gridTemplateRows: 'repeat(6, 38px)',
    justifyContent: 'center',
  },
  numericDay: {
    transition: 'opacity 0.2s ease-in-out',
    width: 38,
    height: 38,
    borderRadius: '50%',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    '&[data-current-month="false"]': {
      opacity: 0.5,
    },
    '&[data-selected="true"]': {
      background: '#7B39FF',
      color: '#fff',
    },
    '&[data-disabled="true"]': {
      cursor: 'not-allowed',
      opacity: 0.3,
    },
    '&:hover': {
      opacity: 1,
      color: '#fff',
      background: '#7B39FF',
    },
  },
}));

export default function TiendadaCalendar(props: CalendarProps) {
  const {initialDate, onChange, minDate} = props;
  const classes = useStyles();

  const normalizedMinDate = minDate ? normalizeToMidnight(minDate) : null;
  const innerInitialDate = initialDate || new Date();

  const [currentDate, setCurrentDate] = useState(innerInitialDate);
  const [daySelected, setDaySelected] = useState({
    dayOfMonth: innerInitialDate.getDate(),
    isCurrentMonth: true,
  });
  const [allDays, setAllDays] = useState<Day[]>(
    generateCalendarDays(innerInitialDate, normalizedMinDate)
  );

  const handlePrevMonthClick = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1
    );
    if (!normalizedMinDate || newDate >= normalizedMinDate) {
      setCurrentDate(newDate);
      onChange(newDate);
      setDaySelected({dayOfMonth: newDate.getDate(), isCurrentMonth: true});
      setAllDays(generateCalendarDays(newDate, normalizedMinDate));
    }
  };

  const handleNextMonthClick = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1
    );
    setCurrentDate(newDate);
    onChange(newDate);
    setDaySelected({dayOfMonth: newDate.getDate(), isCurrentMonth: true});
    setAllDays(generateCalendarDays(newDate, normalizedMinDate));
  };

  const handleClickToday = () => {
    const today = new Date();
    const normalizedToday = normalizeToMidnight(today);
    if (!normalizedMinDate || normalizedToday >= normalizedMinDate) {
      setCurrentDate(normalizedToday);
      setAllDays(generateCalendarDays(normalizedToday, normalizedMinDate));
      onChange(normalizedToday);
      setDaySelected({
        dayOfMonth: normalizedToday.getDate(),
        isCurrentMonth: true,
      });
    }
  };

  const handleSelectedDay = (day: Day) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day.dayOfMonth
    );
    const normalizedNewDate = normalizeToMidnight(newDate);
    if (!normalizedMinDate || normalizedNewDate >= normalizedMinDate) {
      onChange(normalizedNewDate);
      setDaySelected(day);
    }
  };

  return (
    <aside className={classes.container}>
      <header className={classes.header}>
        <span className={classes.calendarCurrentMonth}>
          {formatDay(currentDate)}
        </span>
        <div className={classes.calendarNavigation}>
          <button
            className={classes.calendarNavigationButton}
            onClick={handlePrevMonthClick}
            disabled={
              normalizedMinDate &&
              new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() - 1,
                1
              ) < normalizedMinDate
            }
          >
            <NavArrowLeftIcon />
          </button>
          <button className={classes.todayButton} onClick={handleClickToday}>
            Hoy
          </button>
          <button
            className={classes.calendarNavigationButton}
            onClick={handleNextMonthClick}
          >
            <NavArrowRightIcon />
          </button>
        </div>
      </header>

      <div className={classes.weekDayContainer}>
        {weekDaysNames.map(day => (
          <span className={classes.weekDay} key={day}>
            {day[0]}
          </span>
        ))}
      </div>

      <div className={classes.dayContainer}>
        {allDays.map(day => {
          const dayDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day.dayOfMonth
          );
          const normalizedDayDate = normalizeToMidnight(dayDate);
          const isDisabled =
            normalizedMinDate && normalizedDayDate < normalizedMinDate;
          return (
            <button
              className={classes.numericDay}
              type="button"
              data-current-month={day.isCurrentMonth}
              data-selected={
                day.dayOfMonth === daySelected?.dayOfMonth && day.isCurrentMonth
              }
              data-disabled={isDisabled}
              onClick={() => !isDisabled && handleSelectedDay(day)}
              key={`${day.dayOfMonth}-${day.isCurrentMonth.toString()}`}
              disabled={isDisabled}
            >
              {day.dayOfMonth}
            </button>
          );
        })}
      </div>
    </aside>
  );
}
